import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Send, ArrowLeft, MessageSquare, Mail } from 'lucide-react';
import { Application } from '../lib/supabase';

type Message = {
  id: string;
  application_id: string;
  agency_id: string;
  sender: 'agency' | 'candidate';
  body: string;
  read: boolean;
  created_at: string;
};

type Props = {
  application: Application;
  onBack: () => void;
};

export default function Messaging({ application, onBack }: Props) {
  const { agency } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMessages();
  }, [application.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadMessages() {
    if (!agency) return;
    setLoading(true);
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('application_id', application.id)
      .order('created_at', { ascending: true });
    setMessages(data ?? []);
    setLoading(false);

    // Mark candidate messages as read
    if (data && data.some((m) => m.sender === 'candidate' && !m.read)) {
      await supabase
        .from('messages')
        .update({ read: true })
        .eq('application_id', application.id)
        .eq('sender', 'candidate');
    }
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim() || !agency) return;
    setSending(true);

    const { data } = await supabase
      .from('messages')
      .insert({
        application_id: application.id,
        agency_id: agency.id,
        sender: 'agency',
        body: body.trim(),
      })
      .select()
      .single();

    if (data) {
      setMessages((prev) => [...prev, data]);
      setBody('');
    }
    setSending(false);
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="w-9 h-9 bg-blue-100 rounded-md flex items-center justify-center flex-shrink-0">
          <span className="text-blue-700 font-bold text-xs">
            {application.candidate_name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-900 truncate">{application.candidate_name}</p>
          <p className="text-xs text-gray-400 truncate">{application.candidate_email}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <MessageSquare className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">No messages yet</p>
            <p className="text-xs text-gray-400">Send a message to start the conversation.</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === 'agency' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] px-4 py-2.5 rounded-lg text-sm ${
                  msg.sender === 'agency'
                    ? 'bg-blue-600 text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}
              >
                <p className="leading-relaxed">{msg.body}</p>
                <p className={`text-xs mt-1 ${msg.sender === 'agency' ? 'text-blue-200' : 'text-gray-400'}`}>
                  {new Date(msg.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-2 pt-3 border-t border-gray-100">
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 px-4 py-2.5 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={sending || !body.trim()}
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md transition-colors flex-shrink-0"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
